import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, KeepTogether, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
export const metadata = {
    templateKey: 'forhandlingsoverenskommelse_3part',
    version: 'v1',
    category: 'Forhandlingsoverenskommelse-3part',
    title: 'Förhandlingsöverenskommelse om årlig hyreshöjning – treparts · Version 2026',
    shortTitle: 'Förhandlingsöverenskommelse — treparts',
    formNumber: 'Formulär R-05-3',
    preamble: 'Parterna i den förhandlingsordning som gäller för huset, har efter förhandlingar om den årliga revideringen av hyran för Huset kommit överens om följande.',
    status: 'active',
    signingPlacementAnchors: 'per-party-label',
    recommendPersonalNumberInSigningBlock: true,
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärdspart',
            role: 'lessor',
            ordinal: 0,
            required: true,
        },
        {
            id: 'property_assoc',
            label: 'Fastighetsägarförening',
            role: 'other',
            ordinal: 0,
            required: true,
            help: 'Den fastighetsägarförening som företräder hyresvärdsparten i trepartsförhandlingen.',
        },
        {
            id: 'tenant_org',
            label: 'Hyresgästorganisation',
            role: 'lessee',
            ordinal: 0,
            required: true,
            help: 'Organisationen som företräder hyresgästerna i förhandlingsordningen.',
        },
    ],
    // Party projection (ADR-0065). Three org-ish parties, full contact set.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'Hyresvärdspart',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'landlord_name',
                    requiredAtFinal: true,
                    help: 'Fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address', requiredAtFinal: true },
                { attr: 'email', key: 'landlord_email', requiredAtFinal: true },
                { attr: 'phone', key: 'landlord_phone' },
            ],
        },
        {
            slot: 'property_assoc',
            section: 'Fastighetsägarförening',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'property_assoc_name',
                    requiredAtFinal: true,
                    help: 'Föreningens namn',
                },
                { attr: 'id_number', key: 'property_assoc_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'property_assoc_address', requiredAtFinal: true },
                { attr: 'email', key: 'property_assoc_email', requiredAtFinal: true },
                { attr: 'phone', key: 'property_assoc_phone' },
            ],
        },
        {
            slot: 'tenant_org',
            section: 'Hyresgästorganisation',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'tenant_org_name',
                    requiredAtFinal: true,
                    help: 'Hyresgästorganisationens namn',
                },
                { attr: 'id_number', key: 'tenant_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'tenant_org_address', requiredAtFinal: true },
                { attr: 'email', key: 'tenant_org_email', requiredAtFinal: true },
                { attr: 'phone', key: 'tenant_org_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'fastigheter',
            label: 'Fastigheter (Huset)',
            multi: true,
            required: true,
            help: 'Lägg till alla fastigheter som omfattas av denna överenskommelse.',
        },
    ],
};
export const variableSchema = [
    // Parter (Hyresvärdspart / Fastighetsägarförening / Hyresgästorganisation)
    // — generated from metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // Hus — multi-property aggregated
    {
        key: 'property_designations',
        label: 'Fastighetsbeteckningar',
        type: 'textarea',
        section: 'Hus',
        requiredAtFinal: true,
        autofill: { kind: 'property_list', field: 'designations' },
        scopes: { imported: true },
    },
    {
        key: 'property_addresses',
        label: 'Samtliga adresser på fastigheterna',
        type: 'textarea',
        section: 'Hus',
        requiredAtFinal: true,
        autofill: { kind: 'property_list', field: 'addresses' },
        help: 'En adress per rad. Hämtas automatiskt från länkade fastigheter.',
        scopes: { imported: true },
    },
    // Överenskommelse
    {
        key: 'increase_type',
        label: 'Höjningstyp',
        type: 'select',
        section: 'Överenskommelse',
        requiredAtFinal: true,
        default: 'Samtliga lägenheter',
        options: [
            {
                value: 'Samtliga lägenheter',
                label: 'Samma höjning för samtliga lägenheter (rekommenderas)',
                code: 'samtliga_lagenheter',
                summary: 'A single percentage increase applies to every apartment in the house.',
            },
            {
                value: 'Per lägenhet',
                label: 'Höjning per lägenhet',
                code: 'per_lagenhet',
                summary: 'Increase is set individually per apartment via a table.',
            },
        ],
    },
    {
        key: 'rent_increase_percent',
        label: 'Höjning (procent)',
        type: 'number',
        min: 0,
        max: 100,
        section: 'Överenskommelse',
        requiredAtFinal: true,
        help: 'T.ex. 4,5',
        visibleWhen: { increase_type: 'Samtliga lägenheter' },
        // Negotiated outcome of this agreement — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'effective_date',
        label: 'Höjningen gäller från',
        type: 'date',
        section: 'Överenskommelse',
        requiredAtFinal: true,
        visibleWhen: { increase_type: 'Samtliga lägenheter' },
        // Per the LLM prompt, `dates.start` on a förhandlingsöverenskommelse
        // is the rent-hike effective date (when "höjs … från den").
        scopes: { imported: { from: 'dates.start', requiredForReview: true }, copy: 'manual' },
    },
    {
        key: 'apartment_increases',
        label: 'Höjning per lägenhet',
        type: 'textarea',
        section: 'Överenskommelse',
        requiredAtFinal: true,
        help: 'Lista per lägenhet, t.ex.\n101 — 4,5 %\n102 — 3,8 %',
        visibleWhen: { increase_type: 'Per lägenhet' },
        // Per-agreement negotiated figures — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // IMD-revidering (all optional — section header only renders if any value is set)
    {
        key: 'imd_water',
        label: 'IMD — Varmvatten',
        type: 'text',
        section: 'IMD-revidering',
        help: 'Ange i kr/m³. Lämna tomt om inte tillämpligt.',
        // Per-round negotiated tariff — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'imd_heat',
        label: 'IMD — Värme',
        type: 'text',
        section: 'IMD-revidering',
        help: 'Lämna tomt om inte tillämpligt.',
        // Per-round negotiated tariff — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'imd_electricity',
        label: 'IMD — El',
        type: 'text',
        section: 'IMD-revidering',
        help: 'Lämna tomt om inte tillämpligt.',
        // Per-round negotiated tariff — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Underrättelse
    {
        key: 'notice_type_overens',
        label: 'Underrättelse till hyresgäster',
        type: 'select',
        section: 'Underrättelse',
        requiredAtFinal: true,
        default: 'Hyresvärd',
        options: [
            {
                value: 'Hyresvärd',
                label: 'Hyresvärden underrättar hyresgästerna',
                code: 'hyresvard',
                summary: 'The landlord alone notifies the tenants of the agreement.',
            },
            {
                value: 'Gemensam skrivelse',
                label: 'Hyresvärden och Hyresgästorganisationen i gemensam skrivelse',
                code: 'gemensam_skrivelse',
                summary: 'Landlord and tenant organisation jointly notify the tenants in one letter.',
            },
        ],
    },
    // Eventuella kopplade avtal
    {
        key: 'linked_agreements',
        label: 'Kopplade avtal',
        type: 'textarea',
        section: 'Eventuella kopplade avtal',
        help: 'Hänvisning till t.ex. tillvalsavtal eller bilagor. Lämna tomt om inga.',
    },
];
export const sampleValues = {
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100\n111 60 Stockholm',
    landlord_email: 'forhandling@sodernabolag.se',
    landlord_phone: '08-123 45 67',
    property_assoc_name: 'Fastighetsägarna Stockholm',
    property_assoc_org_number: '802001-2345',
    property_assoc_address: 'Alströmergatan 14\n112 47 Stockholm',
    property_assoc_email: 'forhandling@fastighetsagarna.se',
    property_assoc_phone: '08-617 75 00',
    tenant_org_name: 'Hyresgästföreningen',
    tenant_org_number: '802001-5106',
    tenant_org_address: 'Norrlandsgatan 7\n111 43 Stockholm',
    tenant_org_email: 'forhandling@hyresgastforeningen.se',
    tenant_org_phone: '0771-443 443',
    property_designations: 'Söderhuset 14\nSöderhuset 15',
    property_addresses: 'Götgatan 35\n116 21 Stockholm\nGötgatan 37\n116 21 Stockholm',
    increase_type: 'Samtliga lägenheter',
    rent_increase_percent: 4.5,
    effective_date: '2027-01-01',
    imd_water: '28,50',
    notice_type_overens: 'Hyresvärd',
    linked_agreements: 'Tillvalsavtal enligt Bilaga 1.',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
function Heading(props) {
    return (_jsx("h2", { className: "mt-4 mb-1.5 border-b border-stone-300 pb-1 text-[12px] font-bold", style: { breakInside: 'avoid' }, children: props.children }));
}
const CHECK_ON = '☑';
const CHECK_OFF = '☐';
export function Letterhead() {
    return (_jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber }));
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, signatories, }) {
    const isSamtliga = !values.increase_type || values.increase_type === 'Samtliga lägenheter';
    const isPerLgh = values.increase_type === 'Per lägenhet';
    const noticeIsLandlord = !values.notice_type_overens || values.notice_type_overens === 'Hyresvärd';
    const showImd = cond(mode, Boolean(values.imd_water || values.imd_heat || values.imd_electricity));
    const showLinked = cond(mode, Boolean(values.linked_agreements));
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "1. Hyresv\u00E4rdspart" }), _jsx("div", { className: "grid grid-cols-2 gap-x-6", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "2. Fastighets\u00E4garf\u00F6rening" }), _jsx(PartyBlock, { metadata: metadata, slot: "property_assoc", values: values })] }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "3. Hyresg\u00E4storganisation" }), _jsx(PartyBlock, { metadata: metadata, slot: "tenant_org", values: values })] })] }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "4. Hus" }), _jsxs("p", { className: "mt-1 mb-1 whitespace-pre-line text-[10.5px] leading-relaxed", children: ["Fastighet/fastigheter:", '\n', _jsx("span", { className: "font-serif", children: v(values.property_designations) })] }), _jsxs("p", { className: "mb-0 whitespace-pre-line text-[10.5px] leading-relaxed", children: ["med f\u00F6ljande adresser:", '\n', _jsx("span", { className: "font-serif", children: v(values.property_addresses) }), ' ', "(\"Huset\")"] })] }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "5. \u00D6verenskommelse" }), _jsxs("div", { className: "mb-2 flex items-start gap-2 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "shrink-0", children: mode === 'blank' ? CHECK_OFF : isSamtliga ? CHECK_ON : CHECK_OFF }), _jsxs("p", { className: "m-0", children: ["\u00C5rshyran f\u00F6r samtliga l\u00E4genheter i Huset h\u00F6js med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.rent_increase_percent) }), ' ', "procent fr\u00E5n den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.effective_date) }), "."] })] }), _jsxs("div", { className: "mb-3 flex items-start gap-2 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "shrink-0", children: mode === 'blank' ? CHECK_OFF : isPerLgh ? CHECK_ON : CHECK_OFF }), _jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "m-0 mb-1", children: "\u00C5rshyran f\u00F6r l\u00E4genheterna i Huset h\u00F6js enligt f\u00F6ljande:" }), _jsxs("table", { className: "w-full border-collapse text-[10px]", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b border-stone-300", children: [_jsx("td", { className: "py-0.5 pr-2 font-sans text-stone-500", children: "L\u00E4genhetsnummer" }), _jsx("td", { className: "py-0.5 font-sans text-stone-500", children: "H\u00F6jning i procent" })] }) }), _jsx("tbody", { children: values.apartment_increases ? (values.apartment_increases
                                                    .split('\n')
                                                    .map((line) => line.trim())
                                                    .filter((line) => line.length > 0)
                                                    .map((line, i) => {
                                                    const [apt, ...rest] = line.split(/\s*[—–-]\s*/);
                                                    const increase = rest.join(' — ');
                                                    return (_jsxs("tr", { className: "border-b border-stone-100", children: [_jsx("td", { className: "py-1 pr-2 font-serif", children: apt }), _jsx("td", { className: "py-1 font-serif", children: increase })] }, i));
                                                })) : ([0, 1, 2].map((i) => (_jsxs("tr", { className: "border-b border-stone-100", children: [_jsx("td", { className: "py-1 pr-2", children: _jsx("span", { className: "inline-block min-w-[120px] border-b border-stone-300", children: "\u00A0" }) }), _jsx("td", { className: "py-1", children: _jsx("span", { className: "inline-block min-w-[80px] border-b border-stone-300", children: "\u00A0" }) })] }, i)))) })] })] })] }), showImd && (_jsxs("div", { className: "mb-3 border-l-2 border-stone-200 pl-3", children: [_jsx("p", { className: "m-0 mb-1 font-sans text-[9px] text-stone-500", children: "Revidering av IMD-debitering enligt f\u00F6ljande (om till\u00E4mpligt):" }), [
                                ['Varmvatten', values.imd_water, 'kr/m³'],
                                ['Värme', values.imd_heat, ''],
                                ['El', values.imd_electricity, ''],
                            ].map(([label, value, unit]) => (_jsxs("div", { className: "mb-0.5 flex gap-2 text-[10.5px] leading-relaxed", children: [_jsxs("span", { className: "min-w-[140px] text-stone-500", children: [label, ":"] }), _jsxs("span", { className: "font-serif", children: [v(value), value && unit ? ` ${unit}` : ''] })] }, label)))] })), _jsxs("div", { className: "text-[10.5px] leading-relaxed", children: [_jsxs("div", { className: "mb-1 flex items-baseline gap-2", children: [_jsx("span", { children: mode === 'blank'
                                            ? CHECK_OFF
                                            : noticeIsLandlord
                                                ? CHECK_ON
                                                : CHECK_OFF }), _jsx("span", { children: "Det \u00E5ligger Hyresv\u00E4rden att underr\u00E4tta hyresg\u00E4sterna om inneh\u00E5llet i denna \u00F6verenskommelse." })] }), _jsxs("div", { className: "mb-2 flex items-baseline gap-2", children: [_jsx("span", { children: mode === 'blank'
                                            ? CHECK_OFF
                                            : values.notice_type_overens === 'Gemensam skrivelse'
                                                ? CHECK_ON
                                                : CHECK_OFF }), _jsx("span", { children: "Hyresv\u00E4rden och Hyresg\u00E4storganisationen underr\u00E4ttar hyresg\u00E4sterna om inneh\u00E5llet i denna \u00F6verenskommelse i en gemensam skrivelse." })] }), _jsx("p", { className: "m-0 text-stone-700", children: "Om \u00F6verenskommelsen p.g.a. felaktig uppgift, f\u00F6rbiseende eller annan liknande anledning f\u00E5tt ett oriktigt inneh\u00E5ll, ska parterna medverka till att r\u00E4ttelse sker snarast." })] })] }), showLinked && (_jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "6. Eventuella kopplade avtal" }), _jsx("p", { className: "m-0 whitespace-pre-line text-[10.5px] leading-relaxed", children: v(values.linked_agreements) })] })), _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                        { label: 'Hyresvärdspart', role: 'lessor', ordinal: 0 },
                        { label: 'Fastighetsägarförening', role: 'other', ordinal: 0 },
                        { label: 'Hyresgästorganisation', role: 'lessee', ordinal: 0 },
                    ], signatories: signatories }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map